import { Injectable } from '@angular/core';
import { PieChartType } from './impact.service';
import tinycolor from 'tinycolor2';

@Injectable({
  providedIn: 'root',
})
export class ColorsService {
  // Main colors - Custom Design System 400 for better clarity
  readonly TRANSPORT_COLOR = '#6C9587'; // primary-400 (more pronounced teal-green)
  readonly TRANSPORT_MAIN_COLOR = '#6C9587'; // primary-400 (more pronounced teal-green)
  readonly ACCOMMODATION_COLOR = '#E0A329'; // warning-400 (more intense yellow)
  readonly ACTIVITY_COLOR = '#45B7D1'; // teal/cyan custom (better contrast with transport)
  readonly FOOD_COLOR = '#F06C47'; // error-400 (brighter orange)
  readonly WASTE_COLOR = '#B1B5B1'; // greyscale-400 (more contrasted gray)
  readonly EMPTY_COLOR = '#CFD2CF'; // greyscale-300 (placeholder for zero-emission charts)

  // Colors for main transport vs on-site transport view (based on primary palette)
  readonly MAIN_TRANSPORT_COLOR = '#527A6D'; // primary-500 (darker version)
  readonly ONSITE_TRANSPORT_COLOR = '#93B5A8'; // primary-300 (lighter version)

  // Color pools for sub-sections (Custom Design System variations)
  readonly TRANSPORT_POOL = ['#BCD3CA', '#93B5A8', '#6C9587', '#527A6D']; // primary-200,300,400,500
  readonly ACCOMMODATION_POOL = ['#F1DA97', '#E9BF5B', '#E0A329', '#D18F21']; // warning-200,300,400,500
  readonly ACTIVITY_POOL = ['#B3E5F4', '#7FD4E8', '#45B7D1', '#2A9AB8']; // teal custom gradient
  readonly FOOD_POOL = ['#F9C6AF', '#F59E7C', '#F06C47', '#EB431E']; // error-200,300,400,500
  readonly WASTE_POOL = ['#E3E7E3', '#CFD2CF', '#B1B5B1', '#8C928C']; // greyscale-200,300,400,500

  // Corresponding Tailwind classes for direct use
  readonly TRANSPORT_CLASS = 'bg-warning-500';
  readonly ACCOMMODATION_CLASS = 'bg-primary-500';
  readonly ACTIVITY_CLASS = 'bg-primary-400'; // Note: custom hex color #45B7D1 used in charts
  readonly FOOD_CLASS = 'bg-validation-500';
  readonly WASTE_CLASS = 'bg-greyscale-600';

  constructor() {
    // No need to read CSS variables anymore
  }

  getColors(detail: { type: string; impact: number }[], type: PieChartType) {
    if (type === 'overview') {
      return this.getOverviewColors(detail);
    } else {
      return this.getDetailColors(detail, type);
    }
  }

  private colorFromType(type: string, baseColor: string): string {
    let hash = 0;
    for (let i = 0; i < type.length; i++) {
      hash = type.charCodeAt(i) + ((hash << 5) - hash);
    }
    const variation = Math.abs(hash % 30) - 15;

    return variation >= 0
      ? tinycolor(baseColor).lighten(variation).toString()
      : tinycolor(baseColor).darken(-variation).toString();
  }

  getOverviewColors(detail: { type: string; impact: number }[]) {
    const colors: string[] = [];
    detail.forEach((d) => {
      console.log(d.type);
      switch (true) {
        case ['mainTransportPerTraveller', 'mainTransportImpact'].includes(
          d.type,
        ):
          colors.push(d.impact > 0 ? this.TRANSPORT_COLOR : this.EMPTY_COLOR);
          break;
        case ['onSiteTransportPerTraveller', 'onSiteTransportImpact'].includes(
          d.type,
        ):
          colors.push(
            d.impact > 0 ? this.TRANSPORT_MAIN_COLOR : this.EMPTY_COLOR,
          );
          break;
        case d.type === 'plane':
          console.log('plane found');
          colors.push(d.impact > 0 ? this.TRANSPORT_POOL[0] : this.EMPTY_COLOR);
          break;
        case d.type === 'train':
          colors.push(d.impact > 0 ? this.TRANSPORT_POOL[1] : this.EMPTY_COLOR);
          break;
        case d.type === 'car':
          colors.push(d.impact > 0 ? this.TRANSPORT_POOL[2] : this.EMPTY_COLOR);
          break;
        case d.type === 'twoWheels':
          colors.push(d.impact > 0 ? this.TRANSPORT_POOL[3] : this.EMPTY_COLOR);
          break;
        case d.type === 'bus':
          colors.push(d.impact > 0 ? this.TRANSPORT_POOL[0] : this.EMPTY_COLOR);
          break;
        case d.type === 'boat':
          colors.push(d.impact > 0 ? this.TRANSPORT_POOL[1] : this.EMPTY_COLOR);
          break;
        case ['accommodationPerTraveller', 'accommodationImpact'].includes(
          d.type,
        ):
          colors.push(
            d.impact >= 0 ? this.ACCOMMODATION_COLOR : this.EMPTY_COLOR,
          );
          break;
        case ['activityPerTraveller', 'activityImpact'].includes(d.type):
          colors.push(d.impact >= 0 ? this.ACTIVITY_COLOR : this.EMPTY_COLOR);
          break;
        case ['foodPerTraveller', 'foodImpact'].includes(d.type):
          colors.push(d.impact > 0 ? this.FOOD_COLOR : this.EMPTY_COLOR);
          break;
        case ['wastePerTraveller', 'wasteImpact'].includes(d.type):
          colors.push(d.impact > 0 ? this.WASTE_COLOR : this.EMPTY_COLOR);
          break;
        default:
          colors.push(this.EMPTY_COLOR);
          break;
      }
    });
    return colors;
  }

  getDetailColors(
    detail: { type: string; impact: number }[],
    type: PieChartType,
  ) {
    let colors: string[] = [];
    switch (type) {
      case 'transport':
        if (detail.length === 1) {
          // For a single element, use the main color (400)
          colors = [this.TRANSPORT_COLOR];
        } else {
          colors = detail
            .slice(0, 4)
            .map(
              (d, index) => this.TRANSPORT_POOL[index] || this.TRANSPORT_COLOR,
            );
        }
        break;
      case 'accommodation':
        if (detail.length === 1) {
          colors = [this.ACCOMMODATION_COLOR];
        } else {
          colors = detail
            .slice(0, 4)
            .map(
              (d, index) =>
                this.ACCOMMODATION_POOL[index] || this.ACCOMMODATION_COLOR,
            );
        }
        break;
      case 'activity':
        if (detail.length === 1) {
          colors = [this.ACTIVITY_COLOR];
        } else {
          colors = detail
            .slice(0, 4)
            .map(
              (d, index) => this.ACTIVITY_POOL[index] || this.ACTIVITY_COLOR,
            );
        }
        break;
      case 'food':
        if (detail.length === 1) {
          colors = [this.FOOD_COLOR];
        } else {
          colors = detail
            .slice(0, 4)
            .map((d, index) => this.FOOD_POOL[index] || this.FOOD_COLOR);
        }
        break;
      case 'waste':
        if (detail.length === 1) {
          colors = [this.WASTE_COLOR];
        } else {
          colors = detail
            .slice(0, 4)
            .map((d, index) => this.WASTE_POOL[index] || this.WASTE_COLOR);
        }
        break;
    }
    return colors;
  }

  withAlpha(color: string, alpha: number): string {
    const tc = tinycolor(color);
    const rgba = tc.toRgb();
    return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${alpha})`;
  }

  shuffleArray(array: string[]): string[] {
    const shuffledArray = array.slice();
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [
        shuffledArray[j],
        shuffledArray[i],
      ];
    }
    return shuffledArray;
  }

  getEmptyColor() {
    return this.EMPTY_COLOR;
  }

  // Generate unique colors for a given number of elements
  generateUniqueColors(count: number): string[] {
    const colors: string[] = [];

    // Use an extended HSL-based palette to generate distinct colors
    for (let i = 0; i < count; i++) {
      const hue = ((i * 360) / Math.max(count, 1)) % 360;
      const saturation = 60 + (i % 4) * 15; // More pronounced saturation variation
      const lightness = 45 + (i % 5) * 12; // More extended lightness variation
      const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      colors.push(color);
    }

    return colors;
  }

  // Improved version of getDetailColors to support more colors
  getExtendedDetailColors(
    details: { type: string; impact: number }[],
    type: PieChartType,
  ): string[] {
    if (details.length <= 4) {
      // Use existing colors for 4 elements or less
      return this.getDetailColors(details, type);
    }

    // Generate unique colors for all elements
    return this.generateUniqueColors(details.length);
  }

  // Method to get Tailwind classes
  getCategoryClass(
    category: 'transport' | 'accommodation' | 'activity' | 'food' | 'waste',
  ): string {
    switch (category) {
      case 'transport':
        return this.TRANSPORT_CLASS;
      case 'accommodation':
        return this.ACCOMMODATION_CLASS;
      case 'activity':
        return this.ACTIVITY_CLASS;
      case 'food':
        return this.FOOD_CLASS;
      case 'waste':
        return this.WASTE_CLASS;
      default:
        return 'bg-greyscale-200';
    }
  }

  // Method to get the hex color of a category
  getCategoryColor(
    category: 'transport' | 'accommodation' | 'activity' | 'food' | 'waste',
  ): string {
    switch (category) {
      case 'transport':
        return this.TRANSPORT_COLOR;
      case 'accommodation':
        return this.ACCOMMODATION_COLOR;
      case 'activity':
        return this.ACTIVITY_COLOR;
      case 'food':
        return this.FOOD_COLOR;
      case 'waste':
        return this.WASTE_COLOR;
      default:
        return this.EMPTY_COLOR;
    }
  }

  // Generates a color gradient for a given category
  // Elements with higher impact have darker colors
  generateCategoryGradient(
    category: 'transport' | 'accommodation' | 'activity' | 'food' | 'waste',
    count: number,
  ): string[] {
    if (count <= 0) return [];
    if (count === 1) return [this.getCategoryColor(category)];

    const baseColor = this.getCategoryColor(category);
    const colors: string[] = [];

    // Special case for 2 elements: use stronger contrast
    if (count === 2) {
      colors.push(tinycolor(baseColor).darken(10).toString()); // Darker for first
      colors.push(tinycolor(baseColor).lighten(15).toString()); // Less light for second
      return colors;
    }

    // Generate shades from darkest to lightest
    // Gradient: from -10% (darker) to +30% (slightly lighter, not white)
    for (let i = 0; i < count; i++) {
      // Calculate percentage in range [-10%, +30%]
      const range = 40; // Total range: 10% darken + 30% lighten
      const offset = -10; // Start at -10% (darker)
      const percentage = offset + (i / (count - 1)) * range;

      let color: string;
      if (percentage < 0) {
        // Darker color
        color = tinycolor(baseColor).darken(-percentage).toString();
      } else {
        // Lighter color
        color = tinycolor(baseColor).lighten(percentage).toString();
      }
      colors.push(color);
    }

    return colors;
  }

  // Generates colors for elements grouped by category with gradient
  generateCategoryGroupedColors(
    elements: Array<{
      category: 'transport' | 'accommodation' | 'activity' | 'food' | 'waste';
      impact: number;
      type: string;
    }>,
  ): string[] {
    // Special case for 2 elements from different categories
    if (elements.length === 2) {
      const categories = [...new Set(elements.map((e) => e.category))];
      if (categories.length === 2) {
        // Two elements from different categories: use main colors with good contrast
        return elements.map((element) => {
          const baseColor = this.getCategoryColor(element.category);
          // Higher impact is darker, lower impact keeps base color
          return elements.indexOf(element) === 0
            ? tinycolor(baseColor).darken(8).toString()
            : baseColor;
        });
      }
    }

    // Group elements by category
    const grouped = elements.reduce(
      (acc, element) => {
        if (!acc[element.category]) {
          acc[element.category] = [];
        }
        acc[element.category].push(element);
        return acc;
      },
      {} as Record<string, typeof elements>,
    );

    const colors: string[] = [];

    // For each category, sort by descending impact and assign gradient colors
    Object.keys(grouped).forEach((category) => {
      const categoryElements = grouped[category as keyof typeof grouped];

      // Sort by descending impact
      categoryElements.sort((a, b) => b.impact - a.impact);

      // Generate gradient colors for this category
      const categoryColors = this.generateCategoryGradient(
        category as
          | 'transport'
          | 'accommodation'
          | 'activity'
          | 'food'
          | 'waste',
        categoryElements.length,
      );

      // Assign colors in the original order of elements
      categoryElements.forEach((element, index) => {
        const originalIndex = elements.findIndex((e) => e === element);
        if (originalIndex !== -1) {
          colors[originalIndex] = categoryColors[index];
        }
      });
    });

    return colors;
  }

  // Utility method to get a darker shade for important elements
  getDarkerShade(baseColor: string, percentage: number = 15): string {
    return tinycolor(baseColor).darken(percentage).toString();
  }

  // Utility method to get a lighter shade for less important elements
  getLighterShade(baseColor: string, percentage: number = 15): string {
    return tinycolor(baseColor).lighten(percentage).toString();
  }
}
